<template>
  <form class="rd-screen tp-grid cu-topup" novalidate @submit.prevent="submitTopup">
    <div class="tp-form">
      <!-- 1. Limit na koji ide kredit -->
      <section class="tp-step tp-step--limit" aria-labelledby="tp-h-limit">
        <div class="tp-step__head">
          <span class="tp-step__n" aria-hidden="true">1</span>
          <h2 id="tp-h-limit">{{ $t("limit") }}</h2>
        </div>

        <div v-if="limitsLoading" class="tp-limits" aria-busy="true">
          <div v-for="n in 2" :key="n" class="rd-skeleton tp-skeleton"></div>
        </div>
        <p v-else-if="!limitCards.length" class="tp-note">{{ $t("company_report.topup.no_limits") }}</p>
        <div v-else class="tp-limits" role="radiogroup" aria-labelledby="tp-h-limit">
          <label v-for="card in limitCards" :key="card.id" class="tp-limit" :class="{ 'is-on': isLimitOn(card) }">
            <input
              type="radio"
              name="tp-limit"
              class="rd-visually-hidden"
              :checked="isLimitOn(card)"
              @change="selectedLimit = card.option"
            />
            <v-icon size="22">mdi-credit-card-outline</v-icon>
            <span class="tp-limit__text">
              <b>{{ card.name }}</b>
              <small v-if="card.detail">{{ card.detail }}</small>
              <small class="tp-limit__scope">
                <v-icon size="14">mdi-store-outline</v-icon>
                {{ card.scope }}
              </small>
            </span>
            <v-icon class="tp-limit__tick" size="20">mdi-check-circle</v-icon>
          </label>
        </div>
      </section>

      <!-- 2. Radnici koji dobijaju kredit -->
      <section class="tp-step tp-step--workers" aria-labelledby="tp-h-workers">
        <div class="tp-step__head">
          <span class="tp-step__n" aria-hidden="true">2</span>
          <h2 id="tp-h-workers">{{ $t("employees") }}</h2>
          <span class="tp-step__meta rd-num">
            {{ $t("company_report.topup.selected", { n: selectedUsers.length, total: users.length }) }}
          </span>
        </div>

        <div v-if="usersLoading" class="tp-workers__list" aria-busy="true">
          <div v-for="n in 6" :key="n" class="rd-skeleton tp-skeleton tp-skeleton--row"></div>
        </div>
        <p v-else-if="!users.length" class="tp-note">{{ $t("company_report.topup.no_workers") }}</p>
        <div v-else class="tp-workers">
          <div class="tp-workers__bar">
            <label class="rd-search tp-search">
              <v-icon size="18" color="#8a817a">mdi-magnify</v-icon>
              <input
                v-model="workerSearch"
                type="search"
                :placeholder="$t('company_report.topup.search')"
                :aria-label="$t('company_report.topup.search')"
                autocomplete="off"
                @keydown.enter.prevent
              />
            </label>
            <button type="button" class="ui-btn ui-btn--sm" :disabled="!shownUsers.length" @click="selectShown">
              {{ $t("select_all") }}
            </button>
            <button
              type="button"
              class="ui-btn ui-btn--sm ui-btn--ghost"
              :disabled="!selectedUsers.length"
              @click="selectedUsers = []"
            >
              {{ $t("company_report.topup.deselect") }}
            </button>
          </div>

          <ul v-if="shownUsers.length" ref="workerList" class="tp-workers__list">
            <li v-for="user in shownUsers" :key="user.id">
              <label class="tp-worker" :class="{ 'is-on': isSelected(user.id) }">
                <input v-model="selectedUsers" type="checkbox" class="rd-visually-hidden" :value="user.id" />
                <v-icon size="20">{{ isSelected(user.id) ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}</v-icon>
                <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ user.initials }}</span>
                <span class="tp-worker__name" :title="user.name">{{ user.name }}</span>
              </label>
            </li>
          </ul>
          <p v-else class="tp-note">{{ $t("company_report.topup.no_match") }}</p>
        </div>
      </section>

      <!-- 3. Iznos -->
      <section class="tp-step tp-step--amount" aria-labelledby="tp-h-amount">
        <div class="tp-step__head">
          <span class="tp-step__n" aria-hidden="true">3</span>
          <h2 id="tp-h-amount">{{ $t("amount") }}</h2>
        </div>
        <div class="tp-amount">
          <label class="tp-money" :class="{ 'is-error': Boolean(amountError) }">
            <input
              v-model="topupAmount"
              type="number"
              inputmode="decimal"
              min="0"
              step="0.01"
              placeholder="0.00"
              :aria-label="$t('amount')"
              :aria-invalid="String(Boolean(amountError))"
            />
            <span class="tp-money__cur">{{ currency }}</span>
          </label>
          <div class="tp-quick" role="group" :aria-label="$t('quick_amounts')">
            <button
              v-for="quick in quickAmounts"
              :key="quick"
              type="button"
              :aria-pressed="String(amountValue === quick)"
              @click="setQuickAmount(quick)"
            >
              {{ quick }} {{ currency }}
            </button>
          </div>
        </div>
        <p v-if="amountError" class="tp-error" role="alert">{{ amountError }}</p>
      </section>

      <!-- 4. Ko je dopunio i kako je plaćeno -->
      <section class="tp-step tp-step--pay" aria-labelledby="tp-h-payment">
        <div class="tp-step__head">
          <span class="tp-step__n" aria-hidden="true">4</span>
          <h2 id="tp-h-payment">{{ $t("company_report.topup.payment") }}</h2>
        </div>
        <div class="tp-pay">
          <fieldset class="tp-field">
            <legend>{{ $t("topped_by") }}</legend>
            <ui-segment v-model="toppedBy" :options="toppedByOptions" />
          </fieldset>
          <fieldset class="tp-field">
            <legend>{{ $t("payment_method") }}</legend>
            <ui-segment v-model="paymentMethod" :options="paymentMethods" />
          </fieldset>
        </div>
      </section>
    </div>

    <!-- Pregled dopune -->
    <aside class="tp-summary ui-card rd-card" :aria-label="$t('company_report.topup.summary')">
      <h2 class="tp-summary__title">{{ $t("company_report.topup.summary") }}</h2>

      <dl class="tp-facts">
        <div class="tp-fact" :class="{ 'is-empty': !selectedLimit }">
          <dt>{{ $t("limit") }}</dt>
          <dd v-if="selectedLimit">
            {{ selectedLimit.name }}
            <small>{{ restaurantLabel(selectedLimit) }}</small>
          </dd>
          <dd v-else>–</dd>
        </div>
        <div class="tp-fact" :class="{ 'is-empty': !selectedUsers.length }">
          <dt>{{ $t("employees") }}</dt>
          <dd v-if="selectedUsers.length">
            <span class="rd-num">{{ selectedUsers.length }}</span>
            <small>{{ selectedPreview }}</small>
          </dd>
          <dd v-else>–</dd>
        </div>
        <div class="tp-fact" :class="{ 'is-empty': !(amountValue > 0) }">
          <dt>{{ $t("amount") }} · {{ $t("company_report.topup.per_worker") }}</dt>
          <dd v-if="amountValue > 0" class="rd-num">{{ money(amountValue) }} {{ currency }}</dd>
          <dd v-else>–</dd>
        </div>
      </dl>

      <div class="tp-total">
        <span>{{ $t("total") }}</span>
        <b class="rd-num">{{ money(total) }}<small>{{ currency }}</small></b>
      </div>

      <div v-if="submitError" class="rd-alert ui-banner ui-banner--lg" role="alert">
        <v-icon size="20" color="#b42318">mdi-alert-circle-outline</v-icon>
        <div>{{ submitError }}</div>
      </div>
      <p v-else-if="missing.length" class="tp-missing">
        {{ $t("company_report.topup.missing", { list: missing.join(", ") }) }}
      </p>

      <button type="submit" class="ui-btn ui-btn--primary tp-submit" :disabled="!canSubmit">
        <v-icon size="20" color="#ffffff" :class="{ 'is-spinning': submitting }">
          {{ submitting ? "mdi-loading" : "mdi-cash-plus" }}
        </v-icon>
        {{ $t("add_credit") }}
      </button>

      <p v-if="selectedLimit && selectedLimitHint" class="tp-hint">{{ selectedLimitHint }}</p>

      <div v-if="recent.length" class="tp-recent">
        <h3>{{ $t("company_report.topup.recent") }}</h3>
        <ul>
          <li v-for="item in recent" :key="item.key">
            <v-icon size="16" color="#15803d">mdi-check-circle</v-icon>
            <span class="tp-recent__name" :title="item.name">{{ item.name }}</span>
            <b class="rd-num">{{ money(item.amount) }} {{ currency }}</b>
          </li>
        </ul>
      </div>
    </aside>
  </form>
</template>

<script>
import moment from "moment";
import axios from "@/plugins/axios";
import CompanyMultiRestaurantTabTopup from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabTopup.vue";
import { getLimitTypeLabelKey, getTimeLimitLabelKey } from "@/utils/limitHelpers";

const RECENT_MAX = 5;

const initialsOf = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("");

/** Pretraga bez razlike u velikim slovima i kvačicama (č, ć, š, ž, đ). */
const fold = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase();

/**
 * Dodaj kredit (firma "više restorana") na računaru: obrazac po koracima (limit, radnici, iznos, uplata) i pregled
 * dopune sa ukupnim iznosom. Radnike i limite učitava stari tab (nasleđen), a zahtev ka serveru je isti kao u
 * starom obrascu. Radi i za više radnika odjednom: ako neka dopuna ne uspe, ostaju izabrani samo oni radnici, da
 * ponovni pokušaj ne dopuni dvaput one kojima je kredit već dodat. Korisnika iz "Kreditnih korisnika" ljuska
 * prosleđuje kao `preselectedUserId`.
 */
export default {
  name: "CompanyDesktopMultiTopup",
  extends: CompanyMultiRestaurantTabTopup,
  data: () => ({
    workerSearch: "",
    usersLoading: true,
    submitError: "",
    /** Poruka o uspehu (telefon nema obaveštenja na dnu, pa je prikazuje u traci uz dugme). */
    notice: "",
    recent: [],
  }),
  computed: {
    currency() {
      return this.majorityRestaurantCurrency || "";
    },
    /** Dostupni limiti kao kartice: naziv, tip / period i restoran na koji važe. */
    limitCards() {
      return this.limitOptions.map((option) => ({
        id: option.id,
        name: option.name,
        detail: [
          this.$t(getLimitTypeLabelKey(option.params?.limit_definition?.active)),
          this.$t(getTimeLimitLabelKey(option.params?.timing?.active)),
        ]
          .filter(Boolean)
          .join(" · "),
        scope: this.restaurantLabel(option),
        option,
      }));
    },
    toppedByOptions() {
      return [
        { label: this.$t("firm"), value: "Firma" },
        { label: this.$t("employee"), value: "Radnik" },
      ];
    },
    userRows() {
      return this.users
        .map((user) => {
          const name = this.toLatin(user.name || "");
          return { id: user.id, name, initials: initialsOf(name), folded: fold(name) };
        })
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    },
    shownUsers() {
      const wanted = fold(this.workerSearch.trim());
      return wanted ? this.userRows.filter((user) => user.folded.includes(wanted)) : this.userRows;
    },
    selectedPreview() {
      const names = this.userRows.filter((user) => this.selectedUsers.includes(user.id)).map((user) => user.name);
      const shown = names.slice(0, 3).join(", ");
      return names.length > 3 ? `${shown} +${names.length - 3}` : shown;
    },
    /** Iznos zaokružen na dve decimale; 0 dok polje nije ispravno popunjeno. */
    amountValue() {
      const value = parseFloat(this.topupAmount);
      return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
    },
    amountError() {
      const typed = this.topupAmount !== null && this.topupAmount !== "";
      return typed && !(this.amountValue > 0) ? this.$t("amount_must_be_positive") : "";
    },
    total() {
      return Math.round(this.amountValue * this.selectedUsers.length * 100) / 100;
    },
    /** Šta još treba popuniti da bi dugme "Dodaj kredit" radilo. */
    missing() {
      const missing = [];
      if (!this.selectedLimit) missing.push(this.$t("limit"));
      if (!this.selectedUsers.length) missing.push(this.$t("employees"));
      if (!(this.amountValue > 0)) missing.push(this.$t("amount"));
      if (!this.toppedBy) missing.push(this.$t("topped_by"));
      if (!this.paymentMethod) missing.push(this.$t("payment_method"));
      return missing;
    },
    canSubmit() {
      return !this.missing.length && !this.submitting;
    },
  },
  watch: {
    /** Jedan jedini limit se izabere sam. */
    limitCards(cards) {
      if (!this.selectedLimit && cards.length === 1) {
        this.selectedLimit = cards[0].option;
      }
    },
    /** Korisnik iz "Kreditnih korisnika" stiže i kad je obrazac već otvoren. */
    preselectedUserId() {
      this.$nextTick(this.revealSelected);
    },
  },
  mounted() {
    this.$emit("kpis", []);
    this.$emit("loading", false);
  },
  methods: {
    async fetchUsers() {
      this.usersLoading = true;
      try {
        await CompanyMultiRestaurantTabTopup.methods.fetchUsers.call(this);
      } finally {
        this.usersLoading = false;
        this.$nextTick(this.revealSelected);
      }
    },
    isLimitOn(card) {
      return Boolean(this.selectedLimit) && this.selectedLimit.id === card.id;
    },
    isSelected(id) {
      return this.selectedUsers.includes(id);
    },
    /** Restoran na koji važi limit ("Svi restorani" kad nije vezan za jedan). */
    restaurantLabel(limit) {
      if (!limit.restaurant_id) {
        return this.$t("all_restaurants");
      }
      const restaurant = this.companyRestaurants.find((item) => item.id === limit.restaurant_id);
      return this.toLatin((restaurant && restaurant.name) || (limit.restaurant && limit.restaurant.name) || "");
    },
    /** Izabrani radnik iz "Kreditnih korisnika" se u dugačkoj listi dovede u vidno polje. */
    revealSelected() {
      const list = this.$refs.workerList;
      const row = list && list.querySelector(".tp-worker.is-on");
      if (row && this.preselectedUserId) {
        list.scrollTop = Math.max(0, row.offsetTop - list.offsetTop - 8);
      }
    },
    selectShown() {
      this.selectedUsers = [...new Set([...this.selectedUsers, ...this.shownUsers.map((user) => user.id)])];
    },
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    nameOf(id) {
      const row = this.userRows.find((user) => user.id === id);
      return row ? row.name : String(id);
    },
    async submitTopup() {
      if (!this.canSubmit) {
        return;
      }
      this.submitting = true;
      this.submitError = "";
      this.notice = "";
      const limit = this.selectedLimit;
      const price = this.amountValue;
      const date = moment().format("YYYY-MM-DD");
      const ids = [...this.selectedUsers];
      const results = await Promise.allSettled(
        ids.map((userId) =>
          axios.post("/limits/transactions", {
            quantity: 1,
            price,
            notes: "",
            user_id: userId,
            company_id: limit.company_id,
            restaurant_id: limit.restaurant_id,
            limit_id: limit.id,
            date,
            limit_type: "credit",
            shift: "1-smena",
          })
        )
      );
      const added = ids.filter((id, index) => results[index].status === "fulfilled");
      const failed = ids.filter((id, index) => results[index].status === "rejected");
      added.forEach((id) => {
        this.recent.unshift({ key: `${Date.now()}-${id}`, name: this.nameOf(id), amount: price });
      });
      this.recent = this.recent.slice(0, RECENT_MAX);

      if (!failed.length) {
        this.notice = this.$t("credit_added_successfully");
        this.$emit("toast", this.notice);
        this.selectedUsers = [];
        this.topupAmount = null;
      } else {
        this.selectedUsers = failed;
        const reason = results[ids.indexOf(failed[0])].reason;
        const serverText = reason && reason.response && reason.response.data && reason.response.data.message;
        this.submitError = added.length
          ? this.$t("company_report.topup.partial", {
              ok: added.length,
              all: ids.length,
              names: failed.map(this.nameOf).join(", "),
            })
          : serverText || this.$t("error_adding_credit");
      }
      this.submitting = false;
    },
  },
};
</script>
